import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import {
  ColorDto,
  ConfigurationProductDto,
  ConfigurationVariantDto2,
  TotalPriceDto,
} from '@/api-sdk';
import { ReducedVariants } from '@/components/configuration/utils';
import { iconPresent } from '@/components/icons';
import { BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles, buttonStyles } from '@/styles';

@customElement('fg-configuration-free-gift')
@localized()
export class FgConfigurationFreeGift extends LitElement {
  @property({ type: Object })
  product?: ConfigurationProductDto;

  @property({ type: Object })
  worth?: TotalPriceDto | null;

  @property({ type: Array })
  variant?: ConfigurationVariantDto2[];

  @property({ type: Object })
  reducedVariants?: ReducedVariants;

  @property({ type: String })
  locale = 'nl_NL';

  @state()
  variantImage?: string;

  @state()
  color?: ColorDto | null;

  protected firstUpdated(): void {
    if (this.variant && Object.keys(this.variant).length) {
      this.variantImage = this.variant[0].image || '';
      this.color = this.variant[0].color;
    }
  }

  render() {
    if (!this.product || !this.reducedVariants || !this.variant) {
      return null;
    }

    const { name } = this.product;
    const { totalQuantity } = this.reducedVariants;

    return html`
      <div class="free-gift">
        ${this.variantImage
          ? html`
              <div class="image">
                <img
                  src=${this.variantImage || ''}
                  width="100"
                  height="100"
                  alt=${name}
                />
              </div>
            `
          : null}

        <div class="free-gift-content">
          <div>
            <span class="gift-icon">${iconPresent}</span>
            <span class="free-gift-message">
              ${t('content.configuration.freeGift', {
                worth: `${this.worth?.excludingVat.formatted}`,
              })}
            </span>
          </div>
          <div class="free-gift-qty">
            ${totalQuantity}x ${name},
            ${this.color
              ? html`
                  ${this.color.name}
                `
              : null}
          </div>
        </div>

        <div class="free-gift-label">
          ${iconPresent}
          <span class="free-gift-word">${t('content.configuration.free')}</span>
        </div>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    css`
      .free-gift {
        display: flex;
        align-items: center;
        font-family: var(
          --fg-font-family--brand,
          var(--fg-fallback-font-family--brand)
        );
        gap: 8px;
      }

      .image {
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        aspect-ratio: 1 / 1;
        overflow: hidden;
        max-height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      .free-gift-content {
        display: flex;
        gap: 8px;
        flex-direction: column;
        justify-content: center;
      }

      .free-gift-content div:first-of-type {
        display: flex;
        align-items: center;
        color: var(--fg-primary, var(--fg-fallback-primary));
      }

      .free-gift-message {
        font-size: 1rem;
        font-weight: 700;
      }

      .free-gift-qty {
        font-size: 0.875rem;
        font-weight: 700;
      }

      .free-gift-label {
        font-size: 0.875rem;
        display: flex;
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        padding: 4px 8px;
        background-color: var(--fg-primary, var(--fg-fallback-primary));
        color: #fff;
        font-weight: 500;
        position: absolute;
        right: 8px;
        top: 8px;
      }

      .free-gift-word {
        margin-left: 6px;
      }

      .gift-icon svg {
        margin-right: 4px;
        width: 16px;
        height: 16px;
      }

      .free-gift-label svg {
        height: 14px;
        width: 14px;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .free-gift {
          gap: 26px;
          position: relative;
        }

        .free-gift-message {
          font-size: 1.5rem;
        }

        .free-gift-qty {
          font-size: 1.125rem;
        }

        .free-gift-label {
          font-size: 1rem;
          padding: 8px;
          right: 0;
          top: 0;
        }

        .free-gift-label svg {
          width: 16px;
          height: 16px;
        }

        .gift-icon svg {
          margin-right: 4px;
          width: 24px;
          height: 24px;
        }

        .image {
          max-height: 100px;
        }
      }
    `,
  ];
}
